import { Check, Minus } from "lucide-react";
import { createContext, forwardRef, useId } from "react";
import { cn } from "@/lib/utils";

export interface CheckboxProps extends Omit<
	React.InputHTMLAttributes<HTMLInputElement>,
	"type" | "onChange"
> {
	checked?: boolean;
	defaultChecked?: boolean;
	indeterminate?: boolean;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	children?: React.ReactNode;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
	(
		{
			className,
			checked,
			defaultChecked,
			indeterminate = false,
			disabled,
			onChange,
			children,
			id: providedId,
			...props
		},
		ref,
	) => {
		const generatedId = useId();
		const id = providedId || generatedId;

		return (
			<label
				htmlFor={id}
				className={cn(
					"inline-flex items-center gap-2 cursor-pointer select-none",
					disabled && "cursor-not-allowed opacity-50",
					className,
				)}
			>
				<span className="relative inline-flex items-center justify-center">
					<input
						ref={ref}
						type="checkbox"
						id={id}
						checked={checked}
						defaultChecked={defaultChecked}
						disabled={disabled}
						onChange={onChange}
						className="peer sr-only"
						{...props}
					/>
					<span
						className={cn(
							"h-4 w-4 rounded border transition-colors",
							"border-gray-300 dark:border-gray-600",
							"peer-checked:bg-primary-500 peer-checked:border-primary-500",
							"peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/50",
							"peer-disabled:bg-gray-100 dark:peer-disabled:bg-gray-800",
							indeterminate && "bg-primary-500 border-primary-500",
						)}
					>
						{(checked || indeterminate) && (
							<span className="absolute inset-0 flex items-center justify-center text-white">
								{indeterminate ? (
									<Minus className="h-3 w-3" strokeWidth={3} />
								) : (
									<Check className="h-3 w-3" strokeWidth={3} />
								)}
							</span>
						)}
					</span>
				</span>
				{children && <span className="text-sm text-gray-700 dark:text-gray-200">{children}</span>}
			</label>
		);
	},
);

Checkbox.displayName = "Checkbox";

// Checkbox.Group
interface CheckboxGroupContextValue {
	value?: string[];
	disabled?: boolean;
	onChange?: (value: string[]) => void;
}

const CheckboxGroupContext = createContext<CheckboxGroupContextValue>({});

export interface CheckboxGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	options?: Array<{
		label: React.ReactNode;
		value: string;
		disabled?: boolean;
	}>;
	value?: string[];
	defaultValue?: string[];
	onChange?: (value: string[]) => void;
	disabled?: boolean;
	direction?: "horizontal" | "vertical";
}

const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
	(
		{
			className,
			options,
			value,
			defaultValue,
			onChange,
			disabled,
			direction = "horizontal",
			children,
			...props
		},
		ref,
	) => {
		const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue || []);

		const currentValue = value !== undefined ? value : internalValue;

		const handleChange = (itemValue: string, checked: boolean) => {
			const newValue = checked
				? [...currentValue, itemValue]
				: currentValue.filter((v) => v !== itemValue);

			if (value === undefined) {
				setInternalValue(newValue);
			}
			onChange?.(newValue);
		};

		return (
			<CheckboxGroupContext.Provider value={{ value: currentValue, disabled, onChange }}>
				<div
					ref={ref}
					className={cn("flex gap-3", direction === "vertical" && "flex-col", className)}
					role="group"
					{...props}
				>
					{options
						? options.map((option) => (
								<Checkbox
									key={option.value}
									checked={currentValue.includes(option.value)}
									disabled={disabled || option.disabled}
									onChange={(e) => handleChange(option.value, e.target.checked)}
								>
									{option.label}
								</Checkbox>
							))
						: children}
				</div>
			</CheckboxGroupContext.Provider>
		);
	},
);

CheckboxGroup.displayName = "CheckboxGroup";

// Need React import for useState
import React from "react";

export { Checkbox, CheckboxGroup };
